import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Building2, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { getProperties, getCashflow, getCombinedCashflow, formatCurrency } from "@/lib/api";
import { ReformExplainer } from "@/components/ReformExplainer";

const COLORS = ['#547d91', '#5D7A85', '#10B981', '#F59E0B', '#EF4444', '#6366F1'];

export default function Cashflow() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("combined");
  const [cashflow, setCashflow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCashflowLoading, setIsCashflowLoading] = useState(false);
  const [taxableIncome, setTaxableIncome] = useState("");
  const isCombined = selectedProperty === "combined";

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await getProperties();
        setProperties(response.data);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProperties();
  }, []);

  useEffect(() => {
    const fetchCashflow = async () => {
      if (isLoading || properties.length === 0) return;
      setIsCashflowLoading(true);
      try {
        const income = taxableIncome ? parseFloat(taxableIncome) : null;
        const response = isCombined
          ? await getCombinedCashflow(income)
          : await getCashflow(selectedProperty, income);
        setCashflow(response.data);
      } catch (error) {
        console.error("Error fetching cashflow:", error);
      } finally {
        setIsCashflowLoading(false);
      }
    };
    fetchCashflow();
  }, [selectedProperty, taxableIncome, isLoading]);


  const barChartData = cashflow ? [
    { name: "Rental Income", value: cashflow.annual_rental_income, fill: "#10B981" },
    { name: "Expenses", value: -cashflow.annual_expenses, fill: "#EF4444" },
    { name: "Interest", value: -cashflow.annual_interest, fill: "#F59E0B" },
    { name: "Net Cashflow", value: cashflow.net_cashflow, fill: cashflow.net_cashflow >= 0 ? "#547d91" : "#EF4444" },
  ] : [];

  const expenseData = cashflow?.expense_breakdown 
    ? Object.entries({
        ...cashflow.expense_breakdown,
        ...(cashflow.annual_interest > 0 ? { loan_interest: cashflow.annual_interest } : {})
      }).map(([name, value], index) => ({
        name: name.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        value,
        fill: COLORS[index % COLORS.length]
      }))
    : [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-12" data-testid="cashflow-empty">
        <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">
          No Properties Yet
        </h3>
        <p className="text-slate-500 mb-6">
          Add properties to analyze their cashflow
        </p>
        <Link to="/properties/new">
          <Button className="bg-[#547d91] hover:bg-[#456a7a] rounded-full">
            Add Property
          </Button>
        </Link>
      </div>
    );
  }

  const selectedProp = properties.find(p => p.id === selectedProperty);

  // Debounce taxable income input
  const handleTaxableIncomeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setTaxableIncome(value);
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="cashflow-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
            Cashflow Analysis
          </h2>
          <p className="text-slate-500 mt-1">
            Track income, expenses, and net cashflow
          </p>
        </div>
        <Select value={selectedProperty} onValueChange={setSelectedProperty}>
          <SelectTrigger className="w-full sm:w-80" data-testid="property-select">
            <SelectValue placeholder="Select property" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="combined" data-testid="property-select-combined">All properties (combined)</SelectItem>
            {properties.map((property) => (
              <SelectItem key={property.id} value={property.id}>
                {property.address}, {property.suburb}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ReformExplainer />

      {/* Taxable Income Input */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Your Annual Taxable Income (optional)
              </label>
              <p className="text-xs text-slate-500">
                Enter your taxable income for accurate marginal tax rate calculations
              </p>
            </div>
            <div className="relative w-full sm:w-64">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <input
                type="text"
                value={taxableIncome ? Number(taxableIncome).toLocaleString() : ''}
                onChange={handleTaxableIncomeChange}
                placeholder="e.g. 120,000"
                className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#547d91] focus:border-[#547d91] outline-none"
                data-testid="taxable-income-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isCashflowLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      ) : cashflow ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="metric-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="text-sm text-slate-500">Annual Rental</span>
                </div>
                <p className="text-2xl font-bold text-emerald-600" data-testid="annual-rental">
                  {formatCurrency(cashflow.annual_rental_income)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                  <span className="text-sm text-slate-500">Annual Expenses</span>
                </div>
                <p className="text-2xl font-bold text-red-500" data-testid="annual-expenses">
                  {formatCurrency(cashflow.annual_expenses)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-sm text-slate-500">Annual Interest</span>
                </div>
                <p className="text-2xl font-bold text-amber-600" data-testid="annual-interest">
                  {formatCurrency(cashflow.annual_interest)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    cashflow.net_cashflow >= 0 ? "bg-[#547d91]/10" : "bg-red-100"
                  }`}>
                    <DollarSign className={`w-5 h-5 ${
                      cashflow.net_cashflow >= 0 ? "text-[#547d91]" : "text-red-600"
                    }`} />
                  </div>
                  <span className="text-sm text-slate-500">Net Cashflow</span>
                </div>
                <p className={`text-2xl font-bold ${
                  cashflow.net_cashflow >= 0 ? "text-[#547d91]" : "text-red-500"
                }`} data-testid="net-cashflow">
                  {formatCurrency(cashflow.net_cashflow)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Cashflow Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" tickFormatter={(v) => `$${Math.abs(v/1000)}k`} />
                      <YAxis dataKey="name" type="category" width={100} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(Math.abs(value))}
                        contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Expense Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {expenseData.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={expenseData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {expenseData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-slate-500 mb-4">No expenses recorded</p>
                      <Link to={isCombined ? "/properties" : `/properties/${selectedProperty}`}>
                        <Button variant="outline" className="rounded-full">
                          Add Expenses <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tax Benefits */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Tax Benefits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Marginal Tax Rate Info */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-700">Marginal Tax Rate</h4>
                    <span className="text-xl font-bold text-[#547d91]">
                      {cashflow.tax_calculation?.marginal_rate_percent || 37}%
                    </span>
                  </div>
                  {cashflow.tax_calculation?.taxable_income_provided ? (
                    <div className="space-y-1 text-sm">
                      <p className="text-slate-600">
                        Base income: {formatCurrency(cashflow.tax_calculation.base_taxable_income)} 
                        <span className="text-slate-400 ml-1">({cashflow.tax_calculation.base_bracket})</span>
                      </p>
                      <p className="text-slate-600">
                        Property impact: {cashflow.tax_calculation.property_taxable_income >= 0 ? '+' : ''}{formatCurrency(cashflow.tax_calculation.property_taxable_income)}
                      </p>
                      <p className="text-slate-600">
                        Adjusted income: {formatCurrency(cashflow.tax_calculation.adjusted_taxable_income)}
                        <span className="text-slate-400 ml-1">({cashflow.tax_calculation.adjusted_bracket})</span>
                      </p>
                      {cashflow.tax_calculation.bracket_changed && (
                        <p className={`font-medium ${cashflow.tax_calculation.bracket_direction === 'down' ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {cashflow.tax_calculation.bracket_direction === 'down' 
                            ? '↓ Property loss moved you to a lower tax bracket' 
                            : '↑ Property income moved you to a higher tax bracket'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">
                      Enter your taxable income above for accurate rate calculation
                    </p>
                  )}
                </div>

                {cashflow.tax_category_label && !cashflow.is_ppor && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="cashflow-tax-category">
                    <p className="text-sm text-slate-700"><span className="font-medium">Tax category:</span> {cashflow.tax_category_label}</p>
                  </div>
                )}

                {cashflow.is_ppor ? (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="ppor-no-gearing">
                    <h4 className="font-semibold text-slate-800 mb-1">Principal Place of Residence</h4>
                    <p className="text-sm text-slate-600">
                      Your home isn't income-producing, so its costs can't be negatively geared or deducted against your other income.
                    </p>
                  </div>
                ) : cashflow.negative_gearing_benefit > 0 ? (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-semibold text-blue-800 mb-1">Negative Gearing Benefit</h4>
                    <p className="text-2xl font-bold text-blue-600">
                      ~{formatCurrency(cashflow.negative_gearing_benefit)}
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      Tax offset at {cashflow.tax_calculation?.marginal_rate_percent || 37}% marginal rate (deductible against your other income)
                    </p>
                  </div>
                ) : cashflow.negative_gearing_quarantined ? (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100" data-testid="ng-quarantined">
                    <h4 className="font-semibold text-amber-800 mb-1">Rental losses quarantined</h4>
                    <p className="text-2xl font-bold text-amber-700">
                      {formatCurrency(cashflow.loss_carried_forward)}
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      From 1 Jul 2027, this loss can't offset your wages/other income. It can only be
                      offset against residential property income and carried forward to future years.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                    <h4 className="font-semibold text-emerald-800 mb-1">Positively Geared</h4>
                    <p className="text-sm text-emerald-600">
                      {isCombined ? "Your portfolio generates" : "This property generates"} positive cashflow and is not negatively geared.
                    </p>
                  </div>
                )}
                <div className="p-4 bg-[#547d91]/5 rounded-lg border border-[#547d91]/10">
                  <h4 className="font-semibold text-slate-700 mb-1">After-Tax Cashflow</h4>
                  <p className={`text-xl font-bold ${
                    cashflow.after_tax_cashflow >= 0 ? "text-emerald-600" : "text-red-500"
                  }`}>
                    {formatCurrency(cashflow.after_tax_cashflow)}
                    <span className="text-sm font-normal text-slate-500">/year</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading">{isCombined ? "Combined Loan Details" : "Loan Details"}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-500">Monthly Payment</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(cashflow.monthly_loan_payment)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-500">Annual Payments</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(cashflow.annual_loan_payments)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-500">Annual Interest</span>
                  <span className="font-semibold text-amber-600">
                    {formatCurrency(cashflow.annual_interest)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-500">Principal Reduction</span>
                  <span className="font-semibold text-emerald-600">
                    {formatCurrency(cashflow.annual_loan_payments - cashflow.annual_interest)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {isCombined && cashflow.properties_breakdown?.length > 0 && (
            <PropertyBreakdown rows={cashflow.properties_breakdown} onSelect={setSelectedProperty} />
          )}
        </>
      ) : null}
    </div>
  );
}

const PropertyBreakdown = ({ rows, onSelect }) => (
  <Card data-testid="cashflow-property-breakdown">
    <CardHeader>
      <CardTitle className="font-heading">Cashflow by Property</CardTitle>
    </CardHeader>
    <CardContent className="divide-y divide-slate-100">
      {rows.map((r) => (
        <button key={r.id} type="button" onClick={() => onSelect(r.id)}
          className="w-full flex items-center justify-between py-3 text-left hover:bg-slate-50 px-2 rounded-lg transition-colors"
          data-testid={`breakdown-row-${r.id}`}>
          <span className="text-slate-700 flex items-center gap-2">
            {r.name}
            {r.is_ppor && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">PPOR</span>}
          </span>
          <span className="flex items-center gap-3">
            <span className={`font-semibold ${r.net_cashflow >= 0 ? "text-[#547d91]" : "text-red-500"}`}>
              {formatCurrency(r.net_cashflow)}<span className="text-xs font-normal text-slate-400">/yr</span>
            </span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
          </span>
        </button>
      ))}
    </CardContent>
  </Card>
);
